import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const reducedMotionQuery = '(prefers-reduced-motion: reduce)';

const publicStepTargets: Record<string, string> = {
    'competencies-hero': 'competencies-hero',
    'operating-philosophy': 'competencies-step-operating',
    'design-architecture': 'competency-step-03',
    'software-engineer': 'competency-step-04',
    'security-engineer': 'competency-step-05',
    'devops-engineer': 'competency-step-06',
    'cloud-engineer': 'competency-step-07',
    'fractional-cto': 'competency-step-08',
    'strategic-build': 'competency-step-08',
    'technical-co-founder': 'competency-step-09',
    'ai-ml-engineer': 'competency-step-10',
    'automation-engineer': 'competency-step-11',
    'next-step': 'competencies-step-final'
};

const publicFocusByStepId: Record<string, string> = Object.entries(publicStepTargets)
    .reduce<Record<string, string>>((result, [publicId, stepId]) => {
        result[stepId] ??= publicId;
        return result;
    }, {});

const prefersReducedMotion = (): boolean =>
    window.matchMedia(reducedMotionQuery).matches;

const clamp = (value: number): number =>
    Math.min(1, Math.max(0, value));

const getCssVar = (element: HTMLElement, name: string, fallback: string): string => {
    const value = window.getComputedStyle(element).getPropertyValue(name).trim();
    return value.length > 0 ? value : fallback;
};

const setText = (root: HTMLElement, selector: string, value: string | undefined): void => {
    const element = root.querySelector<HTMLElement>(selector);
    if (element && value !== undefined && element.textContent !== value) {
        element.textContent = value;
    }
};

const getTargetScrollTop = (step: HTMLElement, block: ScrollLogicalPosition): number => {
    const rect = step.getBoundingClientRect();
    const absoluteTop = rect.top + window.scrollY;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    return block === 'center'
        ? absoluteTop - Math.max(0, (viewportHeight - rect.height) / 2)
        : absoluteTop;
};

const scrollToStep = (step: HTMLElement, block: ScrollLogicalPosition, behavior: ScrollBehavior): void => {
    window.scrollTo({
        top: Math.max(0, getTargetScrollTop(step, block)),
        behavior
    });
};

const jumpToStep = (step: HTMLElement, block: ScrollLogicalPosition): void => {
    const previousScrollBehavior = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'auto';
    scrollToStep(step, block, 'auto');
    document.documentElement.style.scrollBehavior = previousScrollBehavior;
};

const getHashId = (hashValue = window.location.hash): string => {
    const hash = hashValue.replace('#', '').trim();
    if (!hash) {
        return '';
    }

    try {
        return decodeURIComponent(hash);
    } catch {
        return hash;
    }
};

const getSearchFocusId = (): string => {
    const params = new URLSearchParams(window.location.search);
    return (params.get('focus') ?? params.get('competency') ?? '').trim();
};

const getInitialFocusId = (): string => {
    const initialHash = (window as Window & { __competenciesInitialHash?: string }).__competenciesInitialHash ?? window.location.hash;
    return getHashId(initialHash) || getSearchFocusId();
};

const getStepUrl = (step: HTMLElement, fallbackHash: string | null = null): string => {
    const publicFocus = publicFocusByStepId[step.id];
    const url = new URL(window.location.href);
    url.hash = '';

    if (publicFocus) {
        url.searchParams.set('focus', publicFocus);
    } else if (fallbackHash?.startsWith('#')) {
        url.hash = fallbackHash;
    }

    return `${url.pathname}${url.search}${url.hash}`;
};

const getOrderedSections = (sections: HTMLElement[]): HTMLElement[] =>
    [...sections].sort((a, b) => {
        if (a === b) {
            return 0;
        }

        return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
    });

const getScrollBlockForSection = (section: HTMLElement): ScrollLogicalPosition =>
    section.matches('[data-competency-step]') ? 'center' : 'start';

const resolveStepForId = (root: HTMLElement, sections: HTMLElement[], id: string): HTMLElement | null => {
    if (!id) {
        return null;
    }

    const publicTargetId = publicStepTargets[id];
    if (publicTargetId) {
        const publicStep = sections.find(step => step.id === publicTargetId);
        if (publicStep) {
            return publicStep;
        }
    }

    const directStep = sections.find(step =>
        step.id === id ||
        step.dataset.publicId === id ||
        step.dataset.publicAlias === id ||
        step.dataset.stageVisual === id
    );
    if (directStep) {
        return directStep;
    }

    const target = document.getElementById(id);
    if (!target || !root.contains(target)) {
        return null;
    }

    const aliasTarget = target.dataset.competencyAliasFor;
    if (aliasTarget) {
        return sections.find(step => step.id === aliasTarget) ?? null;
    }

    return target.closest<HTMLElement>('[data-competency-step], [data-competency-special]');
};

const resolveStepFromHref = (root: HTMLElement, sections: HTMLElement[], href: string | null): HTMLElement | null => {
    if (!href?.startsWith('#')) {
        return null;
    }

    return resolveStepForId(root, sections, href.slice(1));
};

const initCompetenciesHeroEntrance = (root: HTMLElement): void => {
    const hero = root.querySelector<HTMLElement>('.competencies-hero[data-competency-special]');
    if (!hero || prefersReducedMotion() || hero.dataset.heroEntranceStarted === 'true') {
        return;
    }

    hero.dataset.heroEntranceStarted = 'true';

    const revealItems = Array.from(hero.querySelectorAll<HTMLElement>('[data-hero-reveal]'));
    const tiles = Array.from(hero.querySelectorAll<HTMLElement>('.competencies-hero__tile'));
    const visuals = hero.querySelector<HTMLElement>('.competencies-hero__visuals');
    let settled = false;

    const settle = (): void => {
        if (settled) {
            return;
        }

        settled = true;
        root.classList.add('is-hero-settled');

        const animatedItems = [
            ...revealItems,
            ...tiles,
            ...(visuals ? [visuals] : [])
        ];

        gsap.killTweensOf(animatedItems);
        gsap.set(revealItems, { autoAlpha: 1, y: 0, filter: 'blur(0px)', clearProps: 'filter,opacity,visibility,transform' });
        gsap.set(tiles, { y: 0, scale: 1, clearProps: 'opacity,visibility,transform' });
        if (visuals) {
            gsap.set(visuals, { scale: 1, clearProps: 'opacity,visibility,transform' });
        }
    };

    gsap.killTweensOf([...revealItems, ...tiles, ...(visuals ? [visuals] : [])]);
    gsap.set(revealItems, { autoAlpha: 0, y: 30, filter: 'blur(10px)' });

    if (visuals) {
        gsap.from(visuals, {
            autoAlpha: 0,
            scale: .985,
            duration: .9,
            ease: 'power2.out',
            clearProps: 'opacity,visibility,transform'
        });
    }

    gsap.timeline({ defaults: { ease: 'power3.out' }, onComplete: settle })
        .from(tiles, {
            autoAlpha: 0,
            y: 24,
            scale: .9,
            duration: 1.05,
            stagger: .055,
            clearProps: 'opacity,visibility,transform'
        }, 0)
        .to(revealItems, {
            autoAlpha: 1,
            y: 0,
            filter: 'blur(0px)',
            duration: .72,
            stagger: .09,
            clearProps: 'filter,opacity,visibility,transform'
        }, .18);

    window.setTimeout(settle, 1900);
};

const initCompetenciesStory = (root: HTMLElement): void => {
    const story = root.querySelector<HTMLElement>('[data-competencies-story]');
    const roleSteps = Array.from(root.querySelectorAll<HTMLElement>('[data-competency-step]'));
    const specialSections = Array.from(root.querySelectorAll<HTMLElement>('[data-competency-special]'));
    const sections = getOrderedSections([...specialSections, ...roleSteps]);
    const visuals = Array.from(root.querySelectorAll<HTMLElement>('[data-competency-stage-visual]'));
    const trackerLinks = Array.from(root.querySelectorAll<HTMLAnchorElement>('[data-competency-tracker-link]'));
    const progress = root.querySelector<HTMLElement>('[data-competencies-progress]');

    if (sections.length === 0) {
        return;
    }

    const reducedMotion = prefersReducedMotion();
    let activeSection: HTMLElement | null = null;
    let activeRoleStep: HTMLElement | null = null;
    let activeIndex = -1;
    let activeRoleIndex = -1;
    let viewportUpdateQueued = false;

    const setRootTone = (step: HTMLElement): void => {
        root.style.setProperty('--competencies-active-accent', getCssVar(step, '--series-accent', '#c9a84c'));
        root.style.setProperty('--competencies-active-rgb', getCssVar(step, '--series-accent-rgb', '201, 168, 76'));
        root.dataset.activeTone = step.dataset.stageTone ?? step.id;
    };

    const setTracker = (step: HTMLElement): void => {
        trackerLinks.forEach(link => {
            const targetStep = resolveStepFromHref(root, sections, link.getAttribute('href'));
            const isActive = targetStep === step;
            link.classList.toggle('is-active', isActive);

            if (isActive) {
                link.setAttribute('aria-current', 'location');
                return;
            }

            link.removeAttribute('aria-current');
        });
    };

    const settleAnimatedItems = (items: HTMLElement[]): void => {
        gsap.killTweensOf(items);
        gsap.set(items, {
            autoAlpha: 1,
            y: 0,
            filter: 'blur(0px)',
            clearProps: 'filter,opacity,visibility,transform'
        });
    };

    const setStageVisual = (step: HTMLElement): HTMLElement | null => {
        const visualKey = step.dataset.stageVisual ?? step.id;
        let activeVisual: HTMLElement | null = null;

        visuals.forEach(visual => {
            const isActive = visual.dataset.competencyStageVisual === visualKey;
            visual.classList.toggle('is-active', isActive);
            if (isActive) {
                activeVisual = visual;
            }
        });

        return activeVisual;
    };

    const animateStage = (step: HTMLElement, direction: number): void => {
        if (reducedMotion) {
            return;
        }

        const stageItems = [
            root.querySelector<HTMLElement>('[data-competencies-stage-kicker]'),
            root.querySelector<HTMLElement>('[data-competencies-stage-backdrop]')
        ].filter((item): item is HTMLElement => Boolean(item));

        gsap.killTweensOf(stageItems);
        gsap.fromTo(
            stageItems,
            { autoAlpha: 0, y: direction * 18, filter: 'blur(10px)' },
            {
                autoAlpha: 1,
                y: 0,
                filter: 'blur(0px)',
                duration: 0.46,
                stagger: 0.035,
                ease: 'power3.out',
                overwrite: 'auto',
                clearProps: 'filter,opacity,visibility,transform'
            }
        );

        const revealItems = Array.from(step.querySelectorAll<HTMLElement>('[data-story-reveal]'));
        if (revealItems.length > 0) {
            gsap.killTweensOf(revealItems);
            gsap.fromTo(
                revealItems,
                { autoAlpha: 0, y: 28, filter: 'blur(10px)' },
                {
                    autoAlpha: 1,
                    y: 0,
                    filter: 'blur(0px)',
                    duration: 0.55,
                    stagger: 0.055,
                    ease: 'power3.out',
                    overwrite: 'auto',
                    clearProps: 'filter,opacity,visibility,transform'
                }
            );
        }

        window.setTimeout(() => {
            if (activeRoleStep !== step) {
                return;
            }

            settleAnimatedItems(stageItems);
            settleAnimatedItems(revealItems);
        }, 780);
    };

    const revealSpecialSection = (section: HTMLElement, direction = 1): void => {
        if (reducedMotion) {
            return;
        }

        if (section.matches('.competencies-hero')) {
            initCompetenciesHeroEntrance(root);
            return;
        }

        const revealItems = Array.from(section.querySelectorAll<HTMLElement>('[data-special-reveal]'));
        if (revealItems.length === 0) {
            return;
        }

        gsap.killTweensOf(revealItems);
        gsap.fromTo(
            revealItems,
            { autoAlpha: 0, y: direction * 30, filter: 'blur(10px)' },
            {
                autoAlpha: 1,
                y: 0,
                filter: 'blur(0px)',
                duration: 0.64,
                stagger: 0.07,
                ease: 'power3.out',
                overwrite: 'auto',
                clearProps: 'filter,opacity,visibility,transform'
            }
        );
    };

    const activateRoleStage = (step: HTMLElement, animate = true): void => {
        const nextRoleIndex = Math.max(0, roleSteps.indexOf(step));
        const direction = activeRoleIndex === -1 || nextRoleIndex >= activeRoleIndex ? 1 : -1;

        if (activeRoleStep === step) {
            return;
        }

        activeRoleStep = step;
        activeRoleIndex = nextRoleIndex;

        setStageVisual(step);

        setText(root, '[data-competencies-stage-kicker]', step.dataset.stageKicker);
        setText(root, '[data-competencies-stage-backdrop]', step.dataset.stageBackdrop ?? step.dataset.stageKicker);

        if (animate) {
            animateStage(step, direction);
        }
    };

    const activate = (step: HTMLElement, animate = true): void => {
        const nextIndex = Math.max(0, sections.indexOf(step));
        const direction = activeIndex === -1 || nextIndex >= activeIndex ? 1 : -1;

        if (activeSection === step) {
            return;
        }

        activeSection = step;
        activeIndex = nextIndex;

        sections.forEach(candidate => candidate.classList.toggle('is-active', candidate === step));
        setRootTone(step);
        setTracker(step);
        root.classList.toggle('is-roles-stage-active', step.matches('[data-competency-step]'));

        if (step.matches('[data-competency-step]')) {
            activateRoleStage(step, animate);
            return;
        }

        if (animate) {
            revealSpecialSection(step, direction);
        }
    };

    const updateActiveFromViewport = (): void => {
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const viewportCenter = viewportHeight * 0.5;
        let bestSection: HTMLElement | null = null;
        let bestScore = Number.POSITIVE_INFINITY;

        sections.forEach(section => {
            const rect = section.getBoundingClientRect();
            if (rect.bottom <= 0 || rect.top >= viewportHeight) {
                return;
            }

            const visibleTop = Math.max(0, rect.top);
            const visibleBottom = Math.min(viewportHeight, rect.bottom);
            const visibleCenter = (visibleTop + visibleBottom) / 2;
            const visibleHeight = Math.max(1, visibleBottom - visibleTop);
            const score = Math.abs(visibleCenter - viewportCenter) - visibleHeight * 0.08;

            if (score < bestScore) {
                bestScore = score;
                bestSection = section;
            }
        });

        if (bestSection) {
            activate(bestSection);
        }
    };

    const requestViewportUpdate = (): void => {
        if (viewportUpdateQueued) {
            return;
        }

        viewportUpdateQueued = true;
        window.requestAnimationFrame(() => {
            viewportUpdateQueued = false;
            updateActiveFromViewport();
        });
    };

    const revealImmediately = (): void => {
        root.querySelectorAll<HTMLElement>('[data-story-reveal], [data-special-reveal], [data-hero-reveal]').forEach(item => {
            item.classList.add('is-visible');
            gsap.set(item, { autoAlpha: 1, y: 0, clearProps: 'filter,opacity,visibility,transform' });
        });
    };

    const initialFocusId = getInitialFocusId();
    const initialStep = resolveStepForId(root, sections, initialFocusId) ?? sections[0];
    root.classList.add('is-tracker-visible');

    if (reducedMotion) {
        root.classList.add('is-reduced-motion');
        activate(initialStep, false);
        revealImmediately();
        progress?.style.setProperty('transform', 'scaleY(1)');
    } else {
        activate(initialStep, false);

        const revealTargets = Array.from(root.querySelectorAll<HTMLElement>('[data-story-reveal], [data-special-reveal]'));
        const heroRevealTargets = Array.from(root.querySelectorAll<HTMLElement>('[data-hero-reveal]'));
        gsap.set(revealTargets, { autoAlpha: 0, y: 28 });
        gsap.set(heroRevealTargets, { autoAlpha: 0, y: 30, filter: 'blur(10px)' });

        if (initialStep.matches('.competencies-hero')) {
            initCompetenciesHeroEntrance(root);
        } else if (initialStep.matches('[data-competency-special]')) {
            revealSpecialSection(initialStep, 1);
        } else {
            gsap.set(initialStep.querySelectorAll<HTMLElement>('[data-story-reveal]'), {
                autoAlpha: 1,
                y: 0,
                clearProps: 'filter,opacity,visibility,transform'
            });
        }

        specialSections.forEach(section => {
            ScrollTrigger.create({
                trigger: section,
                start: 'top 58%',
                end: 'bottom 42%',
                onToggle: self => {
                    if (self.isActive) {
                        activate(section);
                    }
                },
                onEnter: () => activate(section),
                onEnterBack: () => activate(section)
            });
        });

        if (story && roleSteps.length > 0) {
            ScrollTrigger.create({
                trigger: story,
                start: 'top 84%',
                end: 'bottom 18%',
                onEnter: () => root.classList.add('is-story-visible'),
                onEnterBack: () => root.classList.add('is-story-visible'),
                onLeave: () => root.classList.remove('is-story-visible'),
                onLeaveBack: () => root.classList.remove('is-story-visible')
            });
        }

        roleSteps.forEach((step, index) => {
            ScrollTrigger.create({
                trigger: step,
                start: index === 0 ? 'top 70%' : 'top 58%',
                end: 'bottom 42%',
                onToggle: self => {
                    if (self.isActive) {
                        activate(step);
                    }
                },
                onEnter: () => activate(step),
                onEnterBack: () => activate(step)
            });
        });

        if (story && progress) {
            ScrollTrigger.create({
                trigger: story,
                start: 'top top',
                end: 'bottom bottom',
                scrub: true,
                onUpdate: self => {
                    progress.style.transform = `scaleY(${Math.max(0.025, clamp(self.progress)).toFixed(4)})`;
                }
            });
        }

        const stage = root.querySelector<HTMLElement>('[data-competencies-stage]');
        if (story && stage) {
            gsap.to(stage, {
                '--stage-drift': '28px',
                ease: 'none',
                scrollTrigger: {
                    trigger: story,
                    start: 'top top',
                    end: 'bottom bottom',
                    scrub: true
                }
            });
        }
    }

    trackerLinks.forEach(link => {
        link.addEventListener('click', event => {
            const href = link.getAttribute('href');
            const targetStep = resolveStepFromHref(root, sections, href);
            if (!targetStep) {
                return;
            }

            event.preventDefault();
            const nextUrl = getStepUrl(targetStep, href);
            const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;

            if (currentUrl !== nextUrl) {
                window.history.pushState(null, '', nextUrl);
            }

            activate(targetStep);
            scrollToStep(targetStep, getScrollBlockForSection(targetStep), reducedMotion ? 'auto' : 'smooth');
        });
    });

    window.addEventListener('hashchange', () => {
        const targetStep = resolveStepForId(root, sections, getHashId());
        if (targetStep) {
            activate(targetStep);
        }
    });

    window.addEventListener('popstate', () => {
        const targetStep = resolveStepForId(root, sections, getInitialFocusId());
        if (targetStep) {
            activate(targetStep);
            jumpToStep(targetStep, getScrollBlockForSection(targetStep));
        }
    });

    if (initialStep !== sections[0] && initialFocusId) {
        const scrollToInitialStep = (): void => {
            jumpToStep(initialStep, getScrollBlockForSection(initialStep));
            window.history.replaceState(null, '', getStepUrl(initialStep));
            activate(initialStep);
            ScrollTrigger.refresh();
            window.requestAnimationFrame(() => {
                activate(initialStep);
                updateActiveFromViewport();
            });
        };

        window.setTimeout(scrollToInitialStep, reducedMotion ? 80 : 260);
        window.addEventListener('load', () => window.setTimeout(scrollToInitialStep, 120), { once: true });
    }

    window.addEventListener('scroll', requestViewportUpdate, { passive: true });
    window.addEventListener('load', () => {
        ScrollTrigger.refresh();
        requestViewportUpdate();
    }, { once: true });
    window.addEventListener('resize', () => {
        ScrollTrigger.refresh();
        requestViewportUpdate();
    }, { passive: true });
};

const initCompetenciesPage = (): void => {
    const root = document.getElementById('competenciesPage');
    if (!root) {
        return;
    }

    initCompetenciesStory(root);
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCompetenciesPage);
} else {
    initCompetenciesPage();
}
